/**
 * @file TexturePool.ts
 * @role Generic Sprite object pool backed by a single shared Texture. Owns nothing about
 *       lifetime/gravity/curves — it only hands out reset Sprites and takes them back, so any
 *       consumer (ParticleService today, a later ambient-background service) can reuse it
 *       without duplicating pooling logic.
 * @owns The free-list of pooled Sprites and the shared Texture they reference.
 * @reads Nothing external.
 * @neverDoes Know about particle lifetime, curves, or spawn shapes. Decide outcomes.
 * @see src/core/particles/ParticleService.ts (consumer)
 */
import { Sprite, type Texture } from 'pixi.js';

export class TexturePool {
  private readonly texture: Texture;
  private readonly maxSize: number;
  private readonly free: Sprite[] = [];
  private createdCountInternal = 0;

  /**
   * @param texture Shared base texture every pooled sprite renders (tint per-use to preserve
   *   batching rather than swapping textures).
   * @param maxSize Number of sprites pre-allocated at construction and the cap on how many are
   *   retained in the free-list on release. `acquire()` beyond this size still succeeds (creates
   *   an overflow sprite) so callers never block, but overflow sprites are not retained past
   *   their `release()` — they're left for GC rather than growing the pool unboundedly.
   */
  constructor(texture: Texture, maxSize: number) {
    this.texture = texture;
    this.maxSize = Math.max(0, Math.floor(maxSize));
    for (let i = 0; i < this.maxSize; i += 1) {
      this.free.push(this.createSprite());
    }
  }

  private createSprite(): Sprite {
    this.createdCountInternal += 1;
    const sprite = new Sprite(this.texture);
    sprite.anchor.set(0.5);
    sprite.visible = false;
    return sprite;
  }

  /** Returns a reset sprite ready to be positioned and added to a container. */
  acquire(): Sprite {
    const sprite = this.free.pop();
    if (sprite) {
      sprite.visible = true;
      return sprite;
    }
    const overflow = this.createSprite();
    overflow.visible = true;
    return overflow;
  }

  /** Fully resets a sprite and returns it to the free-list (up to `maxSize`; discards overflow). */
  release(sprite: Sprite): void {
    sprite.visible = false;
    sprite.alpha = 1;
    sprite.scale.set(1);
    sprite.rotation = 0;
    sprite.tint = 0xffffff;
    if (sprite.parent) sprite.parent.removeChild(sprite);
    if (this.free.length < this.maxSize) {
      this.free.push(sprite);
    } else {
      sprite.destroy();
    }
  }

  /** Number of sprites currently sitting in the free-list. */
  get freeCount(): number {
    return this.free.length;
  }

  /** Total sprites ever constructed by this pool (pre-allocated + overflow). Test hook for
   *  asserting no unbounded per-frame allocation growth. */
  get createdCount(): number {
    return this.createdCountInternal;
  }

  /** Destroys every pooled sprite. The pool must not be used after this. */
  destroy(): void {
    for (const sprite of this.free) sprite.destroy();
    this.free.length = 0;
  }
}
